<script setup>
import { computed, onMounted, watch } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { useAppStore } from './stores/app'
import { isDemo } from './lib/api'
import { hasSupabaseConfig } from './lib/config'
import { t, setLanguage } from './lib/i18n'
import Icon from './components/Icon.vue'
import LanguageSwitch from './components/LanguageSwitch.vue'
import PwaPrompts from './components/PwaPrompts.vue'

const store = useAppStore()
const route = useRoute()

// Aktiverer oversettelse av hele grensesnittet så snart familiens språk er
// kjent, og bytter live når en forelder endrer det på Familie-siden.
watch(() => store.family?.language, (lang) => setLanguage(lang), { immediate: true })

const parentTabs = [
  { name: 'oversikt', label: () => t('Oversikt'), icon: 'home' },
  { name: 'oppgaver', label: () => t('Oppgaver'), icon: 'tasks' },
  { name: 'utfordringer', label: () => t('Utfordring'), icon: 'trophy' },
  { name: 'familie', label: () => t('Familie'), icon: 'users' },
]
const childTabs = [
  { name: 'minside', label: () => t('Min side'), icon: 'wallet' },
  { name: 'gjore', label: () => t('Gjøremål'), icon: 'star' },
  { name: 'mine-utfordringer', label: () => t('Utfordring'), icon: 'trophy' },
  { name: 'historikk', label: () => t('Historikk'), icon: 'receipt' },
]

const tabs = computed(() => {
  if (!store.profile) return []
  return store.isParent ? parentTabs : childTabs
})

// Fanen skal se aktiv ut også på undersider (f.eks. /barn/:id under Oversikt).
const activeTab = computed(() => {
  if (route.name === 'barn-detalj') return 'oversikt'
  if (route.name === 'minprofil') return 'minside'
  return route.name
})

onMounted(() => {
  // Nettleseren kan ha lagt appen i bakgrunnen; hent ferske tall ved retur.
  // Feiler oppfriskningen - typisk etter lang tid i bakgrunnen, med en
  // innlogging som er utløpt - må det sies fra. Ellers blir gamle tall
  // stående som om de var ferske.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible' || !store.profile) return
    store.refresh().catch((err) => store.notify(t(err.message), 'error'))
  })
})
</script>

<template>
  <div v-if="store.booting" class="center" style="padding-top: 30vh">
    <div class="spinner" style="margin: 0 auto" />
  </div>

  <template v-else>
    <div v-if="isDemo" class="demo-banner">
      {{ t('Demomodus') }} &middot; {{ t('data lagres kun i denne nettleseren') }}
      <!-- Bare når det finnes et ekte oppsett å gå tilbake til. Er API-et
           ikke satt opp, er demoen hele appen. -->
      <a v-if="hasSupabaseConfig" href="/welcome?demo=0">{{ t('Avslutt') }}</a>
    </div>

    <!-- Før innlogging finnes ingen familie å hente språk fra. Knappen lar
         den som kommer hit velge selv, uten å måtte gjette seg gjennom en
         norsk innlogging først. -->
    <div v-if="!store.profile" class="lang-corner" :class="{ 'lang-corner--banner': isDemo }">
      <LanguageSwitch />
    </div>

    <RouterView v-slot="{ Component }">
      <component :is="Component" />
    </RouterView>

    <nav v-if="tabs.length" class="tabbar">
      <RouterLink
        v-for="tab in tabs"
        :key="tab.name"
        :to="{ name: tab.name }"
        class="tabbar__item"
        :class="{ 'is-active': activeTab === tab.name }"
      >
        <span class="tabbar__icon">
          <Icon :name="tab.icon" :size="21" />
          <span v-if="tab.name === 'oversikt' && store.pendingCount" class="tabbar__badge">
            {{ store.pendingCount }}
          </span>
        </span>
        {{ tab.label() }}
      </RouterLink>
    </nav>

    <Transition name="toast">
      <div v-if="store.toast" class="toast" :class="`toast--${store.toast.kind}`">
        {{ store.toast.message }}
      </div>
    </Transition>

    <PwaPrompts />
  </template>
</template>

<style scoped>
.demo-banner {
  position: sticky;
  top: 0;
  z-index: 50;
  padding: 6px 12px;
  text-align: center;
  font-size: 0.78rem;
  font-weight: 600;
  color: #7c4a03;
  background: #fde68a;
}
.demo-banner a { color: inherit; margin-left: 8px; }

.lang-corner {
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 12px);
  right: 14px;
  z-index: 45;
}
/* Demobanneret ligger klistret øverst - da må knappen under det. */
.lang-corner--banner { top: calc(env(safe-area-inset-top, 0px) + 40px); }

.toast {
  position: fixed;
  left: 50%;
  bottom: calc(var(--nav-h) + var(--safe-b) + 16px);
  transform: translateX(-50%);
  z-index: 70;
  max-width: min(420px, calc(100vw - 32px));
  padding: 12px 18px;
  border-radius: 14px;
  font-weight: 600;
  color: #fff;
  background: #232a1c;
  box-shadow: var(--shadow-lg);
}
.toast--ok { background: var(--ok); color: var(--on-accent); }
.toast--error { background: var(--danger); }

.toast-enter-active, .toast-leave-active { transition: all 0.25s ease; }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translate(-50%, 12px); }
</style>
